import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import { serviceDetails, processSteps } from "../mock/mock";

const NotFound = () => (
  <div className="min-h-screen flex flex-col">
    <Header forceSolid />
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-24">
      <p className="display text-7xl font-extrabold uppercase">404</p>
      <p className="mono uppercase tracking-widest text-neutral-500 mt-4">Service not found</p>
      <Link to="/" className="btn-ink mt-8">Back Home</Link>
    </div>
    <Footer />
  </div>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = serviceDetails.find((s) => s.slug === slug);
  useScrollReveal([slug]);

  if (!service) return <NotFound />;

  const Icon = Icons[service.icon] || Icons.Sparkles;
  const others = serviceDetails.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div className="bg-[#F4F1EA]">
      <Header forceSolid />

      {/* Hero */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] pt-36 pb-20 relative overflow-hidden">
        <svg className="absolute -right-24 top-6 w-[560px] opacity-[0.06] spin-slow hidden md:block" viewBox="0 0 52 30" fill="none">
          <path d="M9 15C9 9 15 9 19 15C23 21 29 21 33 15C37 9 43 9 43 15C43 21 37 21 33 15C29 9 23 9 19 15C15 21 9 21 9 15Z" stroke="#F4F1EA" strokeWidth="1.4" fill="none" />
        </svg>
        <div className="container-x relative">
          <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#F4F1EA]/50 mb-8">
            <button onClick={() => navigate("/")} className="hover:text-[#F4F1EA]">Home</button>
            <span>/</span>
            <button onClick={() => navigate("/#services")} className="hover:text-[#F4F1EA]">Services</button>
            <span>/</span>
            <span className="text-[#F4F1EA]">{service.title}</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="w-16 h-16 border-2 border-[#F4F1EA] flex items-center justify-center mb-8">
                <Icon size={30} />
              </div>
              <h1 className="display font-extrabold uppercase leading-[0.92] tracking-tight text-[clamp(2rem,3.4vw,3.1rem)] break-words">
                {service.title}
              </h1>
              <p className="mt-6 text-[#F4F1EA]/70 text-lg max-w-xl leading-relaxed">{service.tagline}</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <button onClick={() => navigate("/#contact")} className="btn-paper group">
                  Start a Project <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => navigate("/#services")} className="btn-outline-paper">All Services</button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-[#F4F1EA] shadow-[10px_10px_0_#F4F1EA]">
                <img src={service.image} alt={service.title} className="w-full h-[360px] object-cover bw" />
              </div>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border-2 border-[#F4F1EA]/25">
            {service.outcomes.map((o, i) => (
              <div key={o} className={`p-5 ${i !== 3 ? "md:border-r-2" : ""} border-[#F4F1EA]/25 ${i < 2 ? "border-b-2 md:border-b-0" : ""} ${i % 2 === 0 ? "border-r-2 md:border-r-0" : ""}`}>
                <Check size={18} className="mb-2" />
                <p className="mono text-[12px] uppercase tracking-wide text-[#F4F1EA]/80">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x grid lg:grid-cols-2 gap-14">
          <div>
            <p className="eyebrow text-neutral-500">/ Overview</p>
            <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95]">
              What You <span className="hl">Get</span>
            </h2>
          </div>
          <div className="space-y-5">
            {service.overview.map((p, i) => (
              <p key={i} className="text-neutral-700 leading-relaxed text-lg">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-pad bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Capabilities</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            Core <span className="hl">Capabilities</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {service.capabilities.map((c, i) => (
              <div key={c.title} className="card-brutal p-7 relative">
                <span className="mono text-[11px] text-neutral-400 absolute top-4 right-5">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="display text-lg font-bold uppercase mb-2">{c.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ How We Work</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            The <span className="hl">Process</span>
          </h2>
          <div className="grid md:grid-cols-4 border-2 border-[#0A0A0A] bg-white">
            {processSteps.map((s, i) => (
              <div key={s.step} className={`p-7 border-[#0A0A0A] ${i !== 3 ? "md:border-r-2" : ""} border-b-2 md:border-b-0`}>
                <span className="display text-4xl font-extrabold stroke-text">{s.step}</span>
                <h3 className="display text-base font-bold uppercase mt-4 mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech */}
      <section className="section-pad bg-[#0A0A0A] text-[#F4F1EA] grid-dots-invert border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-[#F4F1EA]/50">/ Tech Stack</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-10">
            Tools We <span className="hl-invert">Use</span>
          </h2>
          <div className="flex flex-wrap gap-4">
            {service.tech.map((t) => (
              <div key={t} className="display font-bold uppercase px-6 py-4 border-2 border-[#F4F1EA]/40 hover:border-[#F4F1EA] hover:-translate-y-1 transition-all">{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Explore More</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">
            Other <span className="hl">Services</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {others.map((s) => {
              const OIcon = Icons[s.icon] || Icons.Sparkles;
              return (
                <button key={s.slug} onClick={() => navigate(`/services/${s.slug}`)} className="card-brutal p-6 text-left group" data-cursor>
                  <div className="w-11 h-11 border-2 border-[#0A0A0A] flex items-center justify-center mb-5 group-hover:bg-[#0A0A0A] transition-colors">
                    <OIcon size={20} className="group-hover:text-[#F4F1EA] transition-colors" />
                  </div>
                  <h3 className="display text-base font-bold uppercase leading-tight mb-3">{s.title}</h3>
                  <span className="mono text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-2">
                    View <ArrowUpRight size={13} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] py-20">
        <div className="container-x text-center">
          <h2 className="display text-3xl md:text-5xl font-extrabold uppercase leading-[0.95]">
            Ready to <span className="hl-invert">Build</span>?
          </h2>
          <p className="mt-5 text-[#F4F1EA]/70 max-w-xl mx-auto">Tell us about your project and our experts will get back within 24 hours.</p>
          <button onClick={() => navigate("/#contact")} className="btn-paper mt-8 mx-auto group">
            Talk to Our Experts <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
